import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { GaragePageContext } from "../../App";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import Pagination from "../Pagination";
import CustomSelect from "../CustomSelectField/CustomSelect";

const AllGarageLayer = () => {
  const [records, setRecords] = useState([]);
  const [data, setData] = useState(null);
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [update, setUpdate] = useState(0);
  const { garagePage, setGaragePage } = useContext(GaragePageContext);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(0);
  const [search, setSearch] = useState("");
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [query, setQuery] = useState("");
  const totalPages = Math.ceil(total / limit);
  const [openStatusId, setOpenStatusId] = useState(null);

  const statusDropdownRef = useRef(null);
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setOpenStatusId(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    const debounceFunction = setTimeout(() => {
      if (search.trim() !== "") {
        setQuery(search);
        setGaragePage(0); // Reset to first page when searching
      } else {
        setQuery("");
      }
    }, 500);

    return () => clearTimeout(debounceFunction);
  }, [search]);
  const handleStatusToggle = (garageId, newStatus) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to ${newStatus ? "activate" : "deactivate"} this account?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Yes, ${newStatus ? "activate" : "deactivate"} it!`,
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
      customClass: {
        confirmButton: "btn btn-success btn-sm ms-2",
        cancelButton: "btn btn-sm",
        popup: "my-custom-modal",
        title: "my-custom-title",
        icon: "my-custom-icon",
        content: "my-custom-text",
      },
      buttonsStyling: false,
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      const formData = new FormData();
      formData.append("isActive", newStatus);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/garages/${garageId}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          },
        );

        const json = await response.json();

        if (response.ok) {
          setUpdate(update + 1);

          toast.success(json.message || "Garage status updated successfully.");
        } else {
          throw new Error(json.errorMessage || "Failed to update status");
        }
      } catch (error) {
        console.error("Error updating garage status:", error);
        Swal.fire({
          title: "Error",
          text: error.message || "Failed to update garage status",
          icon: "error",
        });
      }
    });
  };

  useEffect(() => {
    const fetchAllGarages = async () => {
      try {
        setLoading(true);
        const searchParams = new URLSearchParams({
          page: garagePage,
          limit: limit,
          search: query, // Use the debounced query value
        });

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/garages?${searchParams}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const json = await response.json();

        if (response.ok) {
          const sortedData = json.data.garages.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at),
          );
          setRecords(sortedData);
          setData(sortedData);
          setTotal(json.data.total);
          setHasNext(json.data.hasNext);
          setHasPrev(json.data.hasPrev);
          setLimit(json.data.limit);
          setLastPage(json.data.page);
        } else {
          setRecords([]);
        }
      } catch (error) {
        console.error("Error fetching garages:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllGarages();
  }, [update, limit, garagePage, query, token]);

  const handleDelete = (garageId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "error",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
      customClass: {
        confirmButton: "btn btn-sm ms-2 delete-confirm-btn",
        cancelButton: "btn btn-sm ms-2",
        popup: "my-custom-modal",
        title: "my-custom-title",
        icon: "my-custom-icon",
        content: "my-custom-text",
      },
      buttonsStyling: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}api/admin/garages/${garageId}`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          if (response.ok) {
            setUpdate(update + 1);
            Swal.fire({
              title: "Deleted!",
              text: "Garage has been deleted.",
              icon: "success",
              customClass: {
                popup: "garage-delete-popup",
                title: "garage-delete-title",
                confirmButton: "garage-delete-button",
              },
            });
          }
        } catch (error) {
          console.error("Error deleting garage:", error);
          Swal.fire({
            title: "Error",
            text: "Failed to delete garage",
            icon: "error",
          });
        }
      }
    });
  };

  const handleEdit = (id) => {
    navigate("/edit-garage/" + id);
  };

  const handleView = (id) => {
    navigate("/garage-details/" + id);
  };

  const getGarageStatusClass = (status) => {
    switch (status) {
      case "approved":
        return "bg-success-focus text-success-600 border-success-main";
      case "rejected":
        return "bg-danger-focus text-danger-600 border-danger-main";
      case "pending":
      default:
        return "bg-warning-focus text-warning-600 border-warning-main";
    }
  };

  const handleApprove = (garageId) => {
    Swal.fire({
      title: "Approve this garage?",
      text: "It will become visible and bookable for car owners.",
      icon: "success",
      showCancelButton: true,
      confirmButtonText: "Yes, approve it!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      customClass: {
        confirmButton: "btn btn-success btn-sm ms-2",
        cancelButton: "btn btn-sm",
        popup: "my-custom-modal",
        title: "my-custom-title",
        icon: "my-custom-icon",
        content: "my-custom-text",
      },
      buttonsStyling: false,
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/garages/${garageId}/approve`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (response.ok) {
          setUpdate(update + 1);
          toast.success("Garage approved successfully.");
        } else {
          const json = await response.json();
          toast.error(json.errorMessage || "Failed to approve garage");
        }
      } catch (error) {
        console.error("Error approving garage:", error);
        toast.error("Failed to approve garage");
      }
    });
  };

  const handleReject = (garageId) => {
    Swal.fire({
      icon: "error",
      title: "Reject this garage",
      input: "text",
      inputLabel: "Rejection reason",
      inputPlaceholder: "e.g. Missing required documents",
      showCancelButton: true,
      confirmButtonText: "Reject",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      customClass: {
        confirmButton: "btn btn-danger btn-sm ms-2",
        cancelButton: "btn btn-sm",
        title: "my-custom-title",
      },
      buttonsStyling: false,
      inputValidator: (value) => {
        if (!value || value.trim().length < 3) {
          return "Reason must be at least 3 characters";
        }
      },
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/garages/${garageId}/reject`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ reason: result.value.trim() }),
          },
        );
        if (response.ok) {
          setUpdate(update + 1);
          toast.success("Garage rejected.");
        } else {
          const json = await response.json();
          toast.error(json.errorMessage || "Failed to reject garage");
        }
      } catch (error) {
        console.error("Error rejecting garage:", error);
        toast.error("Failed to reject garage");
      }
    });
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex flex-wrap align-items-center gap-3">
          <div className="icon-field">
            <input
              type="text"
              className="form-control form-control-sm w-auto"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <span className="icon">
              <Icon icon="ion:search-outline" />
            </span>
          </div>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-3">
          <Link to="/add-garage" className="btn btn-sm btn-primary-600">
            <i className="ri-add-line" /> Add Garage
          </Link>
        </div>
      </div>

      <div className="card-body p-24">
        <div
          className="table-responsive scroll-sm"
          style={{
            overflowX: "auto",
            overflowY: "visible",
            maxWidth: "100%",
          }}
        >
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                {/* <th scope="col">ID</th> */}
                <th scope="col">Garage Name</th>
                <th scope="col">Owner</th>

                <th scope="col">Registration Number</th>
                <th scope="col">Garage Status</th>
                <th scope="col">Date Created</th>
                <th scope="col">Account Status</th>
                <th scope="col" className="text-center">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    Loading...
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    No garages found
                  </td>
                </tr>
              ) : (
                records.map((garage) => (
                  <tr
                    key={garage.id}
                    onClick={() => handleView(garage.id)}
                    className="cursor-pointer"
                  >
                    {/* <td>{garage.id}</td> */}
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {garage.garage_name}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <img
                          src={
                            garage.owner.image_url ||
                            "assets/images/user-list/user-list1.png"
                          }
                          alt="Owner"
                          className="w-40-px h-40-px rounded-circle flex-shrink-0 me-12 overflow-hidden"
                        />
                        <span className="text-md mb-0 fw-normal text-secondary-light">
                          {garage.owner.fullName}
                        </span>
                      </div>
                    </td>
                    {/* <td className="text-center">
                      <span className={`${garage.owner.isActive ? "bg-success-focus text-success-600 border-success-main" : "bg-neutral-200 text-neutral-600 border-neutral-400"} border px-24 py-4 radius-4 fw-medium text-sm`}>
                        {garage.owner.isActive ? "Active" : "Inactive"}
                      </span>
                    </td> */}

                    <td>{garage.business_registration_number}</td>

                    <td>
                      <div
                        ref={(node) => {
                          if (openStatusId === garage.id) {
                            statusDropdownRef.current = node;
                          }
                        }}
                        className="position-relative d-inline-block"
                        style={{
                          zIndex: openStatusId === garage.id ? 1050 : 1,
                        }}
                      >
                        {garage.status !== "rejected" ? (
                          <>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenStatusId(
                                  openStatusId === garage.id ? null : garage.id,
                                );
                              }}
                              className={`garage-status-dropdown ${getGarageStatusClass(
                                garage.status,
                              )}`}
                            >
                              <span>{garage.status}</span>

                              <Icon
                                icon="mdi:chevron-down"
                                className="garage-status-arrow"
                              />
                            </button>

                            {openStatusId === garage.id && (
                              <div className="garage-status-menu">
                                {garage.status === "pending" && (
                                  <button
                                    type="button"
                                    className="garage-status-option"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleApprove(garage.id);
                                      setOpenStatusId(null);
                                    }}
                                  >
                                    <Icon icon="mdi:check" />
                                    <span>Approve</span>
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="garage-status-option garage-status-option-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReject(garage.id);
                                    setOpenStatusId(null);
                                  }}
                                >
                                  <Icon icon="mdi:close" />
                                  <span>Reject</span>
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <span
                            className={`border px-16 py-4 radius-4 fw-medium text-sm text-capitalize ${getGarageStatusClass(
                              garage.status,
                            )}`}
                          >
                            {garage.status}
                          </span>
                        )}
                      </div>
                    </td>

                    <td>
                      {format(new Date(garage.created_at), "dd MMM, yyyy")}
                    </td>
                    <td>
                      {" "}
                      <div className="form-switch switch-success d-flex align-items-center justify-content-center me-auto">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id={`status-switch-${garage.id}`}
                          checked={garage.owner.isActive || false}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() =>
                            handleStatusToggle(
                              garage.id,
                              !garage.owner.isActive,
                            )
                          }
                        />
                        <label
                          className="form-check-label line-height-1 fw-medium text-secondary-light ms-2"
                          htmlFor={`status-switch-${garage.id}`}
                        >
                          {garage.owner.isActive ? "Active" : "Inactive"}
                        </label>
                      </div>
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-10 justify-content-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleView(garage.id);
                          }}
                          className="bg-info-focus bg-hover-primary-200 text-primary-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          title="View Details"
                        >
                          <Icon icon="lucide:eye" className="menu-icon" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(garage.id);
                          }}
                          className="bg-success-focus bg-hover-success-200 text-success-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          title="Edit"
                        >
                          <Icon icon="lucide:edit" className="menu-icon" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(garage.id);
                          }}
                          className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          title="Delete"
                        >
                          <Icon
                            icon="fluent:delete-24-regular"
                            className="menu-icon"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <div className="d-flex align-items-center gap-2">
            <span>
              Showing {records.length} of {total} entries
            </span>
            <CustomSelect
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value));
                setGaragePage(0); // Reset to first page when changing limit
              }}
              options={[
                { value: "10", label: "10" },
                { value: "15", label: "15" },
                { value: "20", label: "20" },
                { value: "50", label: "50" },
                { value: "100", label: "100" },
              ]}
            />
          </div>
          <Pagination
            currentPage={garagePage}
            totalPages={totalPages}
            hasNext={hasNext}
            hasPrev={hasPrev}
            onPageChange={setGaragePage}
          />
        </div>
      </div>
    </div>
  );
};

export default AllGarageLayer;
